import { useCallback, useState } from 'react';
import {
  Grid,
  Panel,
  Box,
  Button,
  OrganizationIcon,
  FilterButton,
  useTranslations,
  useSnackbar,
  usePopper,
  SearchableTextField,
  Tooltip,
  RenderIf,
  ISearchableTextFieldProps,
  IFilterButtonProps,
  ExportButton,
} from '@uniqkey-frontend/shared-app';
import { CreatePartnerOrganizationRequest } from '@uniqkey-backend-partner/api-client';
import ACLEnum from '../../enums/ACLEnum';
import { useUser } from '../../contexts/UserContext';
import usePartnerOrganizationsAPI from '../../hooks/usePartnerOrganizationsAPI';
import useOrganizationsTable from '../../hooks/tables/useOrganizationsTable';
import { useLanguages } from '../../hooks/reactQuery';
import OrganizationsTable from '../../components/tables/OrganizationsTable';
import CreateOrganizationModal from './components/CreateOrganizationModal';
import OrganizationsListFilter, {
  IOrganizationsListFilterSubmitResult,
} from './components/OrganizationsListFilter';
import { isSupport } from '../../helpers/userRole';
import { logException } from '../../services/sentryService';
import DownloadCSVModal, { IDownloadCSVModalSubmitResult } from '../../components/DownloadCSVModal';
import useSubscribeToExportLicenseDataResultNotificationEvent
  from '../../hooks/useSubscribeToExportLicenseDataResultNotificationEvent';

const FIRST_ADMIN_NOT_ACTIVATED_ERROR = 'First_admin_not_activated';

const OrganizationsPage = () => {
  const { userCan, currentUser } = useUser();
  const { role } = currentUser ?? {};

  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] = useState(false);
  const handleCreateOrganizationModalOpen = useCallback(
    () => setIsCreateOrganizationModalOpen(true),
    [],
  );
  const handleCreateOrganizationModalClose = useCallback(
    () => setIsCreateOrganizationModalOpen(false),
    [],
  );
  const [isCreateOrganizationLoading, setIsCreateOrganizationLoading] = useState(false);
  const [isDownloadCSVModalOpen, setIsDownloadCSVModalOpen] = useState(false);
  const [isDownloadCSVLoading, setIsDownloadCSVLoading] = useState(false);
  const handleDownloadCSVModalOpen = useCallback(() => setIsDownloadCSVModalOpen(true), []);
  const handleDownloadCSVModalClose = useCallback(() => setIsDownloadCSVModalOpen(false), []);
  const handleExportLicenseDataSuccess = useCallback(() => {
    handleDownloadCSVModalClose();
    setIsDownloadCSVLoading(false);
  }, [handleDownloadCSVModalClose]);
  const handleExportLicenseDataError = useCallback(() => {
    setIsDownloadCSVLoading(false);
  }, []);

  // preload for create organization modal
  useLanguages({ includeOnlyAllowedLanguages: false });

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );

  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );

  const { t } = useTranslations();
  const { createOrganization, requestOrganizationLicenseDataExport } = usePartnerOrganizationsAPI();
  const { showSuccess, showError } = useSnackbar();

  const {
    selectedOrganizations,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetQuery,
    resetActivePage,
    ...restTableProps
  } = useOrganizationsTable({
    noDataMessageKey: isSupport(role)
      ? 'organizationsPage.supportNoData' : 'organizationsPage.noData',
  });

  const handleCreateOrganization = useCallback(async (
    organization: CreatePartnerOrganizationRequest,
  ) => {
    try {
      setIsCreateOrganizationLoading(true);
      await createOrganization(organization);
      showSuccess({
        text: t('createOrganizationModal.organizationCreated'),
      });
      handleCreateOrganizationModalClose();
      resetQuery();
    } catch (e: any) {
      let key = 'common.somethingWentWrong';
      if (e?.response?.data?.includes(FIRST_ADMIN_NOT_ACTIVATED_ERROR)) {
        key = 'createOrganizationModal.firstAdminNotActivatedError';
      }
      showError({ text: t(key) });
      logException(e, {
        message: 'OrganizationsPage/handleCreateOrganization exception',
      });
    } finally {
      setIsCreateOrganizationLoading(false);
    }
  }, [
    createOrganization,
    handleCreateOrganizationModalClose,
    resetQuery,
    showError,
    showSuccess,
    t,
  ]);

  const handleDownloadCSV = useCallback(async (value: IDownloadCSVModalSubmitResult) => {
    try {
      setIsDownloadCSVLoading(true);
      await requestOrganizationLicenseDataExport(value);
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
      logException(e, {
        message: 'OrganizationsPage/handleDownloadCSV exception',
      });
      setIsDownloadCSVLoading(false);
    }
  }, [requestOrganizationLicenseDataExport, showError, t]);

  useSubscribeToExportLicenseDataResultNotificationEvent({
    from: 'OrganizationsPage',
    onSuccess: handleExportLicenseDataSuccess,
    onError: handleExportLicenseDataError,
  });

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((updatedValues: IOrganizationsListFilterSubmitResult) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  return (
    <Grid container flexDirection="column" className="min-height-100-percent">
      <Grid item mb={1}>
        <Panel>
          <Box p={1}>
            <Grid container justifyContent="space-between" alignItems="stretch">
              <Grid item xs={3} container flexWrap="nowrap" columnGap={1}>
                <Grid item>
                  <Tooltip title={t('common.filter')}>
                    <FilterButton
                      isFilterActive={isFilterActive}
                      numberOfActiveFilters={numberOfActiveFilters}
                      selected={isFilterOpen}
                      onChange={toggleIsFilterOpen}
                    />
                  </Tooltip>
                </Grid>
                {userCan(ACLEnum.OrganizationExportList) && (
                  <Grid item>
                    <Tooltip title={t('downloadCSVModal.tooltip')}>
                      <ExportButton onClick={handleDownloadCSVModalOpen} />
                    </Tooltip>
                  </Grid>
                )}
                {/* TODO: actions here */}
              </Grid>
              <Grid item xs={9} container justifyContent="flex-end" flexWrap="nowrap">
                <Grid item>
                  <SearchableTextField
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={t('common.search')}
                  />
                </Grid>
                <RenderIf condition={userCan(ACLEnum.OrganizationCreate)}>
                  <Box mr={3} />
                  <Grid item>
                    <Button
                      icon={<OrganizationIcon />}
                      onClick={handleCreateOrganizationModalOpen}
                    >
                      {t('organizationsPage.createOrganizationButton')}
                    </Button>
                  </Grid>
                </RenderIf>
              </Grid>
            </Grid>
          </Box>
        </Panel>
      </Grid>
      <Grid item xs>
        <Panel>
          <OrganizationsTable
            selectedOrganizations={selectedOrganizations}
            {...restTableProps}
          />
        </Panel>
      </Grid>
      {isCreateOrganizationModalOpen && (
        <CreateOrganizationModal
          isOpen={isCreateOrganizationModalOpen}
          isLoading={isCreateOrganizationLoading}
          onSubmit={handleCreateOrganization}
          onClose={handleCreateOrganizationModalClose}
        />
      )}
      <OrganizationsListFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
      {isDownloadCSVModalOpen && (
        <DownloadCSVModal
          isOpen={isDownloadCSVModalOpen}
          isLoading={isDownloadCSVLoading}
          onSubmit={handleDownloadCSV}
          onClose={handleDownloadCSVModalClose}
          selectedOrganizations={selectedOrganizations}
        />
      )}
    </Grid>
  );
};

export default OrganizationsPage;
